import { authState, restoreSession } from "@/services/auth";
import {
  clearMemberBillingOverview,
  memberBillingState,
  refreshMemberBillingOverview,
  resolveMemberIdentityLabel,
} from "@/services/memberStatus";
import {
  listCreditPackages,
  listMembershipPlans,
} from "@/services/membershipCatalog";
import {
  refreshMyCreditBalances,
  refreshMyCreditsOverview,
} from "@/services/myCredits";
import { clearMyPaymentOrders, refreshMyPaymentOrders } from "@/services/myPaymentOrders";
import { claimMyMemberPromotion, listMyMemberPromotions } from "@/services/memberPromotions";
import { formatYuanFromCents, yuanFromCents } from "@/utils/currency";

const MEMBER_DISCOUNT_STAGE1 = Object.freeze({
  code: "stage1",
  amount: 20,
  title: "新人专享红包",
  label: "会员套餐自动立减",
  description: "全档位会员下单时自动抵扣",
  scopes: ["day", "week", "month", "quarter", "year", "lifetime"],
});
const MEMBER_DISCOUNT_STAGE2 = Object.freeze({
  code: "stage2",
  amount: 20,
  title: "终身会员红包",
  label: "永久会员专享",
  description: "仅限永久会员下单时自动抵扣",
  scopes: ["lifetime"],
});
const MEMBER_DISCOUNT_STAGE3 = Object.freeze({
  code: "stage3",
  amount: 200,
  title: "红包膨胀券",
  label: "永久会员限时加码",
  description: "仅限永久会员限时自动抵扣",
  scopes: ["lifetime"],
});

const MEMBER_PROMOTION_CARD_TEXT = Object.freeze({
  stage1: {
    title: "新人专享红包",
  },
  stage2: {
    title: "终身会员红包",
  },
  stage3: {
    title: "红包膨胀券",
  },
});

function normalizePromotionCyclesByEdition(raw, fallbackCycles = []) {
  const normalized = {
    personal: [],
    team: [],
  };
  const fallback = Array.isArray(fallbackCycles) ? fallbackCycles.slice() : [];
  const source = raw && typeof raw === "object" ? raw : {};
  ["personal", "team"].forEach((edition) => {
    const items = Array.isArray(source[edition]) ? source[edition] : fallback;
    normalized[edition] = Array.from(new Set(items.filter(Boolean)));
  });
  return normalized;
}

function normalizePromotionDeadlineMs(raw) {
  if (raw == null || raw === "") {
    return 0;
  }
  const direct = Number(raw);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

// Shared by the picture shell and embedded video membership host.
export default {
  data() {
    return {
      showMemberLogin: false,
      memberDialogVisible: false,
      newDialogVisible: false,
      newDialogType: 1,
      pendingMemberEntry: false,
      memberFlowTimer: null,
      stage3Now: Date.now(),
      stage3Timer: null,
      memberCatalogLoading: false,
      memberPromotionLoading: false,
      membershipPlansCatalog: [],
      creditPackagesCatalog: [],
      memberPromotionDefinitions: [],
      memberPromotionGrants: [],
    };
  },
  computed: {
    isAuthenticated() {
      return !!(authState.session && authState.session.id);
    },
    memberUserName() {
      if (!(authState.session && authState.session.user)) {
        return "未登录用户";
      }
      return authState.session.user.display_name || authState.session.user.username || "会员用户";
    },
    memberUserID() {
      if (!(authState.session && authState.session.user)) {
        return "--";
      }
      return authState.session.user.id || authState.session.user.username || "--";
    },
    memberBillingLoading() {
      return memberBillingState.loading;
    },
    memberBillingOverview() {
      return memberBillingState.overview;
    },
    memberIdentity() {
      const user = authState.session && authState.session.user ? authState.session.user : null;
      return resolveMemberIdentityLabel(user, memberBillingState.overview);
    },
    memberLevelCode() {
      const overview = memberBillingState.overview;
      return overview && overview.level_code ? String(overview.level_code).trim() : "free";
    },
    memberExpireLabel() {
      const subscription = this.currentSubscription;
      if (!subscription) {
        return "未开通";
      }
      const plan = subscription.plan || {};
      const planCode = String(plan.code || "").trim().toLowerCase();
      if (planCode === "free") {
        return "未开通";
      }
      if (!subscription.ends_at) {
        return this.isLifetimeSubscription ? "永久有效" : "长期有效";
      }
      return this.formatDateTime(subscription.ends_at);
    },
    memberPromotionMap() {
      return this.memberPromotionDefinitions.reduce((acc, item) => {
        if (item && item.code) {
          acc[item.code] = item;
        }
        return acc;
      }, {});
    },
    memberPromotionGrantMap() {
      return this.memberPromotionGrants.reduce((acc, item) => {
        if (item && item.promotion_code) {
          acc[item.promotion_code] = item;
        }
        return acc;
      }, {});
    },
    stage1Definition() {
      return this.memberPromotionMap.stage1 || null;
    },
    stage2Definition() {
      return this.memberPromotionMap.stage2 || null;
    },
    stage3Definition() {
      return this.memberPromotionMap.stage3 || null;
    },
    stage1Grant() {
      return this.memberPromotionGrantMap.stage1 || null;
    },
    stage2Grant() {
      return this.memberPromotionGrantMap.stage2 || null;
    },
    stage3Grant() {
      return this.memberPromotionGrantMap.stage3 || null;
    },
    stage1Claimed() {
      return !!this.stage1Grant;
    },
    stage2Claimed() {
      return !!this.stage2Grant;
    },
    stage3Claimed() {
      return !!this.stage3Grant;
    },
    hasAnyPermanentDiscount() {
      return !!(this.stage1Claimed || this.stage2Claimed);
    },
    currentSubscription() {
      return this.memberBillingOverview && this.memberBillingOverview.subscription
        ? this.memberBillingOverview.subscription
        : null;
    },
    isLifetimeSubscription() {
      const plan = this.currentSubscription && this.currentSubscription.plan
        ? this.currentSubscription.plan
        : null;
      return String(plan && plan.billing_cycle || "").trim().toLowerCase() === "lifetime";
    },
    isStage3Active() {
      if (!(this.stage3Grant && this.stage3Grant.expires_at)) {
        return false;
      }
      return String(this.stage3Grant.status || "").trim().toLowerCase() === "claimed"
        && new Date(this.stage3Grant.expires_at).getTime() > this.stage3Now;
    },
    isStage3Expired() {
      if (!(this.stage3Grant && this.stage3Grant.expires_at)) {
        return false;
      }
      return new Date(this.stage3Grant.expires_at).getTime() <= this.stage3Now;
    },
    keepMemberDialogOpenOnClose() {
      if (!this.stage1Claimed) {
        return false;
      }
      const stage2Pending = !!(this.stage2Definition && this.stage2Definition.status === "active" && !this.stage2Claimed);
      const stage3Pending = !!(this.stage3Definition && this.stage3Definition.status === "active" && !this.stage3Claimed);
      return stage2Pending || stage3Pending;
    },
    stage3CountdownLabel() {
      if (!this.isStage3Active) {
        return "00:00:00";
      }
      const expiresAt = new Date(this.stage3Grant.expires_at).getTime();
      const diff = Math.max(0, expiresAt - this.stage3Now);
      const hours = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const minutes = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    memberPromotionSaleDeadlineMs() {
      const sources = [
        this.stage3Definition && this.stage3Definition.config,
        this.stage2Definition && this.stage2Definition.config,
        this.stage1Definition && this.stage1Definition.config,
      ];
      for (const source of sources) {
        const value = normalizePromotionDeadlineMs(source && (source.sale_deadline_at_ms || source.sale_deadline_at));
        if (value > 0) {
          return value;
        }
      }
      return 0;
    },
    memberPromotionSaleActive() {
      return this.memberPromotionSaleDeadlineMs > this.stage3Now;
    },
    memberPromotionSaleCountdownLabel() {
      if (!this.memberPromotionSaleDeadlineMs || !this.memberPromotionSaleActive) {
        return "00:00:00";
      }
      const diff = Math.max(0, this.memberPromotionSaleDeadlineMs - this.stage3Now);
      const hours = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const minutes = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    },
    memberPromotionSaleDeadlineLabel() {
      if (!this.memberPromotionSaleDeadlineMs) {
        return "";
      }
      return this.formatDateTime(this.memberPromotionSaleDeadlineMs);
    },
    memberPlans() {
      return this.membershipPlansCatalog.map((plan) => ({
        ...plan,
        currentPrice: yuanFromCents(plan.originalPriceCents || 0),
        currentPriceCents: Number(plan.originalPriceCents || 0),
        discount: 0,
        originalPriceLabel: `${formatYuanFromCents(plan.originalPriceCents || 0)}元`,
      }));
    },
    memberCreditPackages() {
      return this.creditPackagesCatalog.map((item) => ({
        ...item,
        currentPrice: yuanFromCents(item.priceCents || 0),
        currentPriceCents: Number(item.priceCents || 0),
        originalPriceLabel: `${formatYuanFromCents(item.priceCents || 0)}元`,
      }));
    },
    stage1CouponItems() {
      const definition = this.stage1Definition;
      const amount = formatYuanFromCents(definition && definition.amount_cents || 0);
      if (!(definition && definition.status === "active")) {
        return [];
      }
      const matchedPlans = this.memberPlans.filter((plan) => this.promotionAppliesToPlan(definition, plan));
      const plans = matchedPlans.length > 0 ? matchedPlans : this.memberPlans;
      return plans.slice(0, 5).map((plan) => ({
        amount,
        title: plan.title || this.formatCouponPlanTitle(plan),
        tip: this.formatCouponPlanTip(plan),
      }));
    },
    activeDiscounts() {
      const items = [];
      if (this.stage1Claimed && this.stage1Definition && this.stage1Definition.status === "active") {
        items.push({
          ...MEMBER_DISCOUNT_STAGE1,
          title: this.promotionTitle(this.stage1Definition, MEMBER_DISCOUNT_STAGE1.title),
          label: this.promotionTitle(this.stage1Definition, MEMBER_DISCOUNT_STAGE1.label),
          description: this.promotionDescription(this.stage1Definition, MEMBER_DISCOUNT_STAGE1.description),
          amount: yuanFromCents(this.stage1Definition.amount_cents || 0),
          amountCents: Number(this.stage1Definition.amount_cents || 0),
          applicableEditions: Array.isArray(this.stage1Definition.applicable_editions) ? this.stage1Definition.applicable_editions : [],
          applicableBillingCycles: Array.isArray(this.stage1Definition.applicable_billing_cycles) ? this.stage1Definition.applicable_billing_cycles : [],
          applicableCyclesByEdition: normalizePromotionCyclesByEdition(
            this.stage1Definition.config && this.stage1Definition.config.applicable_cycles_by_edition,
            this.stage1Definition.applicable_billing_cycles
          ),
          templateCode: this.stage1Definition.template_code || "stage1",
        });
      }
      if (this.stage2Claimed && this.stage2Definition && this.stage2Definition.status === "active") {
        items.push({
          ...MEMBER_DISCOUNT_STAGE2,
          title: this.promotionTitle(this.stage2Definition, MEMBER_DISCOUNT_STAGE2.title),
          label: this.promotionTitle(this.stage2Definition, MEMBER_DISCOUNT_STAGE2.label),
          description: this.promotionDescription(this.stage2Definition, MEMBER_DISCOUNT_STAGE2.description),
          amount: yuanFromCents(this.stage2Definition.amount_cents || 0),
          amountCents: Number(this.stage2Definition.amount_cents || 0),
          applicableEditions: Array.isArray(this.stage2Definition.applicable_editions) ? this.stage2Definition.applicable_editions : [],
          applicableBillingCycles: Array.isArray(this.stage2Definition.applicable_billing_cycles) ? this.stage2Definition.applicable_billing_cycles : [],
          applicableCyclesByEdition: normalizePromotionCyclesByEdition(
            this.stage2Definition.config && this.stage2Definition.config.applicable_cycles_by_edition,
            this.stage2Definition.applicable_billing_cycles
          ),
          templateCode: this.stage2Definition.template_code || "stage2",
        });
      }
      if (this.isStage3Active) {
        items.push({
          ...MEMBER_DISCOUNT_STAGE3,
          title: this.promotionTitle(this.stage3Definition, MEMBER_DISCOUNT_STAGE3.title),
          label: this.promotionTitle(this.stage3Definition, MEMBER_DISCOUNT_STAGE3.label),
          amount: yuanFromCents(this.stage3Definition && this.stage3Definition.amount_cents || 0),
          amountCents: Number(this.stage3Definition && this.stage3Definition.amount_cents || 0),
          applicableEditions: Array.isArray(this.stage3Definition && this.stage3Definition.applicable_editions) ? this.stage3Definition.applicable_editions : [],
          applicableBillingCycles: Array.isArray(this.stage3Definition && this.stage3Definition.applicable_billing_cycles) ? this.stage3Definition.applicable_billing_cycles : [],
          applicableCyclesByEdition: normalizePromotionCyclesByEdition(
            this.stage3Definition && this.stage3Definition.config && this.stage3Definition.config.applicable_cycles_by_edition,
            this.stage3Definition && this.stage3Definition.applicable_billing_cycles
          ),
          templateCode: this.stage3Definition && this.stage3Definition.template_code || "stage3",
          description: this.promotionDescription(this.stage3Definition, `${this.stage3CountdownLabel} 后恢复常规优惠`),
        });
      }
      return items;
    },
    memberPromotionCards() {
      const items = [];
      if (this.stage1Claimed && this.stage1Definition && this.stage1Definition.status === "active") {
        items.push({
          code: "stage1",
          amount: formatYuanFromCents(this.stage1Definition.amount_cents || 0),
          title: this.promotionTitle(this.stage1Definition, MEMBER_PROMOTION_CARD_TEXT.stage1.title),
          statusText: "",
          expired: false,
        });
      }
      if (this.stage2Claimed && this.stage2Definition && this.stage2Definition.status === "active") {
        items.push({
          code: "stage2",
          amount: formatYuanFromCents(this.stage2Definition.amount_cents || 0),
          title: this.promotionTitle(this.stage2Definition, MEMBER_PROMOTION_CARD_TEXT.stage2.title),
          statusText: "",
          expired: false,
        });
      }
      if (this.stage3Claimed && this.stage3Definition && this.stage3Definition.status === "active") {
        items.push({
          code: "stage3",
          amount: formatYuanFromCents(this.stage3Definition.amount_cents || 0),
          title: this.promotionTitle(this.stage3Definition, MEMBER_PROMOTION_CARD_TEXT.stage3.title),
          statusText: this.isStage3Expired ? "已失效" : "",
          expired: this.isStage3Expired,
        });
      }
      return items;
    },
  },
  methods: {
    resetMemberPromotionState() {
      this.clearMemberFlowTimer();
      this.pendingMemberEntry = false;
      this.memberDialogVisible = false;
      this.newDialogVisible = false;
      this.memberPromotionDefinitions = [];
      this.memberPromotionGrants = [];
    },
    async loadMemberPromotions({ showError = false } = {}) {
      this.memberPromotionLoading = true;
      try {
        const payload = await listMyMemberPromotions();
        this.memberPromotionDefinitions = payload && Array.isArray(payload.definitions) ? payload.definitions : [];
        this.memberPromotionGrants = payload && Array.isArray(payload.grants) ? payload.grants : [];
      } catch (error) {
        this.memberPromotionDefinitions = [];
        this.memberPromotionGrants = [];
        if (showError) {
          this.$message.error(error.message || "加载会员优惠失败");
        }
      } finally {
        this.memberPromotionLoading = false;
      }
    },
    formatCycleLabel(value) {
      switch (value) {
        case "week":
          return "周";
        case "month":
          return "月";
        case "quarter":
          return "季";
        case "year":
          return "年";
        case "lifetime":
          return "终身";
        default:
          return value || "";
      }
    },
    promotionTitle(definition, fallback) {
      const value = String(definition && definition.name || "").trim();
      return value || fallback || "";
    },
    promotionDescription(definition, fallback) {
      const value = String(definition && definition.description || "").trim();
      return value || fallback || "";
    },
    formatEditionLabel(value) {
      switch (value) {
        case "team":
          return "团队版";
        case "personal":
          return "个人版";
        default:
          return value || "";
      }
    },
    formatCouponCycleLabel(value) {
      switch (value) {
        case "day":
          return "体验";
        case "week":
          return "周费";
        case "month":
          return "月费";
        case "quarter":
          return "季费";
        case "year":
          return "年费";
        case "lifetime":
          return "终身";
        default:
          return value || "";
      }
    },
    formatCouponPlanTitle(plan) {
      const cycle = this.formatCouponCycleLabel(plan && plan.billing_cycle);
      return cycle ? `${cycle}会员` : "会员套餐";
    },
    formatCouponPlanTip(plan) {
      const edition = this.formatEditionLabel(plan && plan.audience);
      const cycle = this.formatCouponCycleLabel(plan && plan.billing_cycle);
      const scope = [edition, cycle].filter(Boolean).join("");
      return scope ? `适用于${scope}会员` : "仅限首次升级，过期作废";
    },
    promotionAppliesToPlan(definition, plan) {
      if (!definition || !plan) {
        return false;
      }
      const currentEdition = plan.audience || "personal";
      const editions = Array.isArray(definition.applicable_editions) ? definition.applicable_editions : [];
      if (editions.length > 0 && !editions.includes(currentEdition)) {
        return false;
      }
      const cycleMap = normalizePromotionCyclesByEdition(
        definition.config && definition.config.applicable_cycles_by_edition,
        definition.applicable_billing_cycles
      );
      const scopedCycles = cycleMap[currentEdition] || [];
      const fallbackCycles = Array.isArray(definition.applicable_billing_cycles) ? definition.applicable_billing_cycles : [];
      const cycles = scopedCycles.length > 0 ? scopedCycles : fallbackCycles;
      return cycles.length === 0 || cycles.includes(plan.billing_cycle || "");
    },
    formatValidityLabel(days, cycle) {
      if (days == null) {
        return cycle === "lifetime" ? "永久有效" : "长期有效";
      }
      return `有效期${days}天`;
    },
    formatDateTime(value) {
      if (!value) {
        return "--";
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return "--";
      }
      const pad = (part) => String(part).padStart(2, "0");
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    formatMembershipValidity(cycle) {
      switch (cycle) {
        case "day":
          return "有效期1天";
        case "week":
          return "有效期7天";
        case "month":
          return "有效期30天";
        case "quarter":
          return "有效期90天";
        case "year":
          return "有效期365天";
        case "lifetime":
          return "永久有效";
        default:
          return "会员套餐";
      }
    },
    formatMembershipCreditText(plan) {
      const credits = Number(plan && plan.gift_credits ? plan.gift_credits : 0);
      if (credits <= 0) {
        return "当前套餐无赠送积分";
      }
      return `赠送 ${credits.toLocaleString("zh-CN")} 积分`;
    },
    formatBytes(value) {
      let size = Number(value || 0);
      if (size <= 0) {
        return "0 B";
      }
      const units = ["B", "KB", "MB", "GB", "TB"];
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index += 1;
      }
      const precision = size >= 100 || index === 0 ? 0 : size >= 10 ? 1 : 2;
      return `${size.toFixed(precision)} ${units[index]}`;
    },
    truthyFeature(value) {
      if (typeof value === "boolean") {
        return value;
      }
      if (typeof value === "number") {
        return value > 0;
      }
      const normalized = String(value || "").trim().toLowerCase();
      return ["1", "true", "yes", "enabled", "on"].includes(normalized);
    },
    intFeature(features, keys) {
      for (const key of keys) {
        const value = Number(features && features[key] != null ? features[key] : 0);
        if (value > 0) {
          return value;
        }
      }
      return 0;
    },
    hasFeature(features, keys) {
      return keys.some((key) => this.truthyFeature(features && features[key]));
    },
    extractMembershipBenefits(plan) {
      const rights = Array.isArray(plan && plan.rights) ? plan.rights : [];
      const rightItems = rights
        .map(this.formatMembershipRightValue)
        .filter(Boolean);
      if (rightItems.length > 0) {
        return Array.from(new Set(rightItems));
      }
      const features = plan && plan.features ? plan.features : {};
      const quotas = Array.isArray(plan && plan.quotas) ? plan.quotas : [];
      const items = [];

      const imageRes = [].concat(features.image_resolutions || []).filter(Boolean);
      if (imageRes.length > 0) {
        items.push(`图片 ${imageRes.join(" / ")}`);
      }
      const videoRes = [].concat(features.video_resolutions || []).filter(Boolean);
      if (videoRes.length > 0) {
        items.push(`视频 ${videoRes.join(" / ")}`);
      }
      if (imageRes.length === 0 && this.hasFeature(features, ["image_4k_enabled", "allow_4k", "image_4k"])) {
        items.push("支持 4K 图片");
      }
      if (videoRes.length === 0 && this.hasFeature(features, ["video_hd_enabled", "allow_hd_video", "video_hd"])) {
        items.push("支持高清视频");
      }

      const concurrency = this.intFeature(features, ["concurrent_limit", "max_concurrency", "concurrency"]);
      if (concurrency > 0) {
        items.push(`并发上限 ${this.formatNumber(concurrency)}`);
      }

      const teamSeats = this.intFeature(features, ["team_member_limit", "team_seat_limit", "member_limit"]);
      if (teamSeats > 0) {
        items.push(`团队成员 ${this.formatNumber(teamSeats)} 人`);
      }

      const storageQuota = quotas.find((item) => String(item && item.code || "").trim().toLowerCase() === "storage.bytes");
      if (storageQuota && Number(storageQuota.total_value || 0) > 0) {
        items.push(`存储空间 ${this.formatBytes(storageQuota.total_value)}`);
      }

      const allowedModels = []
        .concat(Array.isArray(features.allowed_model_keys) ? features.allowed_model_keys : [])
        .concat(Array.isArray(features.allowed_creator_model_keys) ? features.allowed_creator_model_keys : [])
        .concat(Array.isArray(features.allowed_director_model_keys) ? features.allowed_director_model_keys : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      if (allowedModels.length > 0) {
        items.push(`专属模型 ${allowedModels.slice(0, 3).join(" / ")}`);
      }

      const maxOutputTokens = this.intFeature(features, ["ai_generate_max_output_tokens", "max_output_tokens"]);
      if (this.truthyFeature(features.ai_generate_enabled) || maxOutputTokens > 0) {
        items.push(maxOutputTokens > 0 ? `AI 文本上限 ${this.formatNumber(maxOutputTokens)} tokens` : "AI 文本生成");
      }

      if (this.hasFeature(features, ["priority_queue"])) {
        items.push("优先队列");
      }
      if (this.hasFeature(features, ["vip_support"])) {
        items.push("专属客服");
      }
      if (this.hasFeature(features, ["fast_response"])) {
        items.push("快速响应");
      }

      const storageBytes = this.intFeature(features, ["storage_bytes"]);
      if (storageBytes > 0 && !items.some((i) => i.startsWith("存储空间"))) {
        items.push(`存储空间 ${this.formatBytes(storageBytes)}`);
      }

      if (items.length === 0) {
        const description = this.formatMembershipCreditText(plan);
        if (description) {
          items.push(description);
        }
      }

      if (items.length === 0) {
        items.push("解锁对应会员级别功能");
      }
      return Array.from(new Set(items)).slice(0, 6);
    },
    formatMembershipRightValue(right) {
      if (!right || typeof right !== "object") {
        return "";
      }
      const name = String(right.name || right.code || "").trim();
      if (!name) {
        return "";
      }
      const value = right.value;
      const type = String(right.value_type || "").trim();
      const unit = String(right.unit || "").trim();
      if (type === "bool" || type === "display") {
        return value ? name : "";
      }
      if (type === "string_list") {
        const list = Array.isArray(value) ? value.map((item) => String(item || "").trim()).filter(Boolean) : [];
        return list.length ? `${name}: ${list.join(" / ")}` : "";
      }
      if (type === "int") {
        const numberValue = Number(value || 0);
        if (!(numberValue > 0)) {
          return "";
        }
        if (String(right.code || "").trim() === "storage_bytes") {
          return `${name}: ${this.formatBytes(numberValue)}`;
        }
        return `${name}: ${this.formatNumber(numberValue)}${unit}`;
      }
      const text = String(value || "").trim();
      return text ? `${name}: ${text}` : name;
    },
    mapMembershipPlan(plan) {
      const benefits = this.extractMembershipBenefits(plan);
      return {
        id: plan.id,
        code: plan.code,
        audience: plan.edition || "personal",
        title: plan.name || "会员套餐",
        billing_cycle: plan.billing_cycle,
        originalPrice: yuanFromCents(plan.price_cents || plan.monthly_price_cents || 0),
        originalPriceCents: Number(plan.price_cents || plan.monthly_price_cents || 0),
        credits: Number(plan.gift_credits || 0),
        validity: this.formatMembershipValidity(plan.billing_cycle),
        creditsText: this.formatMembershipCreditText(plan),
        metaType: "text",
        benefitText: plan.badge || "会员套餐",
        hot: !!plan.badge,
        benefits,
        benefitsSummary: benefits.join(" / "),
        features: plan.features || {},
        quotas: Array.isArray(plan.quotas) ? plan.quotas : [],
        rights: Array.isArray(plan.rights) ? plan.rights : [],
        description: this.formatMembershipCreditText(plan),
      };
    },
    mapCreditPackage(item) {
      const metadata = item.metadata || {};
      return {
        id: item.id,
        code: item.code,
        audience: item.edition || metadata.edition || "personal",
        title: item.name || "积分加油包",
        billing_cycle: metadata.cycle || "",
        price: yuanFromCents(item.price_cents || 0),
        priceCents: Number(item.price_cents || 0),
        credits: Number(item.total_credits || 0),
        validity: this.formatValidityLabel(item.expires_in_days, metadata.cycle),
        metaType: "number",
        benefitText: item.badge || metadata.badge || "积分补充",
        hot: !!(item.badge || metadata.badge),
        description: `到账 ${Number(item.total_credits || 0).toLocaleString("zh-CN")} 积分；有效期：${this.formatValidityLabel(item.expires_in_days, metadata.cycle)}`,
      };
    },
    async loadMembershipCatalog({ showError = false } = {}) {
      this.memberCatalogLoading = true;
      try {
        const [plansPayload, packagesPayload] = await Promise.all([
          listMembershipPlans(),
          listCreditPackages(),
        ]);
        this.membershipPlansCatalog = (plansPayload && plansPayload.plans ? plansPayload.plans : [])
          .filter((item) => item.plan_kind === "membership" && item.is_active)
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
          .map((item) => this.mapMembershipPlan(item));
        this.creditPackagesCatalog = (packagesPayload && packagesPayload.packages ? packagesPayload.packages : [])
          .filter((item) => item.is_active && (item.edition || (item.metadata && item.metadata.edition)))
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
          .map((item) => this.mapCreditPackage(item));
      } catch (error) {
        this.membershipPlansCatalog = [];
        this.creditPackagesCatalog = [];
        if (showError) {
          this.$message.error(error.message || "加载会员数据失败");
        }
      } finally {
        this.memberCatalogLoading = false;
      }
    },
    async loadCurrentSpaceBilling({ showError = false, force = false } = {}) {
      try {
        return await refreshMemberBillingOverview({ force });
      } catch (error) {
        if (showError) {
          this.$message.error(error.message || "加载会员状态失败");
        }
        return null;
      }
    },
    async refreshMemberDialogData(options = {}) {
      await restoreSession();
      if (!this.isAuthenticated) {
        this.resetMemberPromotionState();
        this.stopStage3Timer();
        this.membershipPlansCatalog = [];
        this.creditPackagesCatalog = [];
        clearMyPaymentOrders();
        clearMemberBillingOverview();
        return false;
      }
      this.ensureStage3Timer();
      await Promise.all([
        this.loadMembershipCatalog(options),
        this.loadMemberPromotions(options),
        this.loadCurrentSpaceBilling({
          ...options,
          force: !!options.forceBilling,
        }),
        refreshMyCreditsOverview().catch(() => {}),
        refreshMyCreditBalances().catch(() => {}),
        refreshMyPaymentOrders({ limit: 20, offset: 0 }).catch(() => {}),
      ]);
      return true;
    },
    ensureStage3Timer() {
      this.stopStage3Timer();
      this.stage3Now = Date.now();
      this.stage3Timer = window.setInterval(() => {
        this.stage3Now = Date.now();
      }, 1000);
    },
    stopStage3Timer() {
      if (this.stage3Timer) {
        clearInterval(this.stage3Timer);
        this.stage3Timer = null;
      }
    },
    clearMemberFlowTimer() {
      if (this.memberFlowTimer) {
        clearTimeout(this.memberFlowTimer);
        this.memberFlowTimer = null;
      }
    },
    async handleMemberEntry() {
      if (this.memberCatalogLoading) {
        return;
      }
      if (!this.isAuthenticated) {
        this.pendingMemberEntry = true;
        this.showMemberLogin = true;
        return;
      }
      const ready = await this.refreshMemberDialogData({ showError: true });
      if (!ready) {
        this.pendingMemberEntry = true;
        this.showMemberLogin = true;
        return;
      }
      this.openMemberFlow();
    },
    async handleMemberLoginSuccess() {
      if (!this.pendingMemberEntry) {
        return;
      }
      this.pendingMemberEntry = false;
      this.showMemberLogin = false;
      const ready = await this.refreshMemberDialogData({ showError: true });
      if (!ready) {
        return;
      }
      this.openMemberFlow();
    },
    openMemberFlow() {
      this.clearMemberFlowTimer();
      this.memberDialogVisible = true;
      if (!this.stage1Claimed && this.stage1Definition && this.stage1Definition.status === "active") {
        this.memberFlowTimer = window.setTimeout(() => {
          this.newDialogType = 1;
          this.newDialogVisible = true;
          this.memberFlowTimer = null;
        }, 240);
      }
    },
    handleMemberDialogClosed(payload) {
      const reason = payload && typeof payload === "object"
        ? String(payload.reason || "").trim().toLowerCase()
        : "";
      if (reason === "payment_success") {
        this.newDialogVisible = false;
        return;
      }
      if (!this.stage1Claimed) {
        return;
      }
      if (!this.stage2Claimed && this.stage2Definition && this.stage2Definition.status === "active") {
        this.newDialogType = 2;
        this.newDialogVisible = true;
        return;
      }
      if (!this.stage3Claimed && this.stage3Definition && this.stage3Definition.status === "active") {
        this.newDialogType = 3;
        this.newDialogVisible = true;
      }
    },
    async handleNewDialogClaim(type) {
      const codeMap = {
        1: "stage1",
        2: "stage2",
        3: "stage3",
      };
      const code = codeMap[type];
      if (!code) {
        this.memberDialogVisible = true;
        return;
      }
      try {
        await claimMyMemberPromotion(code);
        await this.loadMemberPromotions({ showError: true });
      } catch (error) {
        this.$message.error(error.message || "领取会员优惠失败");
      } finally {
        this.memberDialogVisible = true;
      }
    },
    async handleMemberPaymentSuccess() {
      this.clearMemberFlowTimer();
      this.newDialogVisible = false;
      await this.refreshMemberDialogData({
        showError: true,
        forceBilling: true,
      });
    },
    formatYuanFromCents,
  },
};
